import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { readCsrfCookie } from './csrf';
import { trackApiFailure, normalizeEndpoint } from '../observability/clarity';
import { captureSentryApiFailure } from '../observability/sentry';

// Fires 'auth:expired' when refresh rotation fails (session truly gone).
export const authEvents = new EventTarget();

const STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export function createApiClient(opts: { baseURL?: string } = {}): AxiosInstance {
  const api = axios.create({ baseURL: opts.baseURL, withCredentials: true });

  // GC-6: attach double-submit CSRF header on state-changing requests; never touch the JWT.
  api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const method = (config.method ?? 'get').toUpperCase();
    if (STATE_CHANGING.has(method)) {
      const tok = readCsrfCookie();
      if (tok) config.headers.set('X-CSRF-Token', tok);
    }
    return config;
  });

  // Single-flight refresh: concurrent 401s share one refresh promise, then replay.
  let refreshing: Promise<void> | null = null;

  const refreshOnce = (): Promise<void> => {
    if (!refreshing) {
      refreshing = axios
        .post('/auth/refresh', null, { baseURL: opts.baseURL, withCredentials: true })
        .then(() => undefined)
        .catch((e) => { authEvents.dispatchEvent(new Event('auth:expired')); throw e; })
        .finally(() => { refreshing = null; });
    }
    return refreshing;
  };

  api.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      const cfg = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
      const isRefreshCall = cfg?.url?.includes('/auth/refresh');

      const status = error.response?.status;
      const statusClass = !status ? 'network'
        : error.code === 'ECONNABORTED' ? 'timeout'
        : status >= 500 ? '5xx'
        : status >= 400 ? '4xx'
        : undefined;

      const endpoint = normalizeEndpoint(cfg?.url ?? 'unknown');
      const method = (cfg?.method ?? 'get').toUpperCase();

      // Clarity: track API failures with safe, normalized context.
      trackApiFailure({
        method,
        endpoint,
        statusCode: status,
        statusClass,
        errorCode: (error.response?.data as { code?: string })?.code,
        retryAttempted: !!cfg?._retried,
      });

      // Sentry: track API failures as breadcrumbs + 5xx as exceptions.
      captureSentryApiFailure({
        method,
        endpoint,
        statusCode: status,
        statusClass,
        errorCode: (error.response?.data as { code?: string })?.code,
      });

      if ((error.response?.status === 401 || error.response?.status === 403) && cfg && !cfg._retried && !isRefreshCall) {
        cfg._retried = true;
        await refreshOnce();
        return api.request(cfg);
      }
      return Promise.reject(error);
    },
  );

  return api;
}
